<template>
  <div class="auth-wrapper auth-v1 px-2">
    <div class="auth-inner py-2">
      <b-card class="mb-0">
        <b-link class="brand-logo">
          <logo />
        </b-link>

        <b-card-title class="mb-1 text-center">
          {{ t('Welcome Panel') }}
        </b-card-title>
        <b-card-text class="mb-2 text-center">
          {{ t('Login Description') }}
        </b-card-text>

        <!--Login form -->
        <validation-observer ref="loginForm">
          <b-form class="auth-login-form mt-2" @submit.prevent="login">
            <!-- email -->
            <b-form-group label-for="Email" :label="t('Email')">
              <validation-provider #default="{ errors }" :name="t('Email')" rules="required|email">
                <b-form-input
                  id="email"
                  v-model="emailValue"
                  name="email"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="t('Email')"
                  tabindex="1"
                  autofocus
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- password -->
            <b-form-group>
              <div class="d-flex justify-content-between">
                <label for="password">{{ t('Password') }}</label>
              </div>
              <validation-provider #default="{ errors }" :name="t('Password')" rules="required">
                <b-input-group
                  class="input-group-merge"
                  :class="errors.length > 0 ? 'is-invalid' : null"
                >
                  <b-form-input
                    id="password"
                    v-model="password"
                    :type="passwordFieldType"
                    class="form-control-merge"
                    :state="errors.length > 0 ? false : null"
                    name="login-password"
                    :placeholder="t('Password')"
                    tabindex="2"
                  />

                  <b-input-group-append is-text>
                    <feather-icon
                      class="cursor-pointer"
                      :icon="passwordToggleIcon"
                      @click="togglePasswordVisibility"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <b-form-group label-for="recaptcha">
              <validation-provider #default="{ errors }" :name="t('Verification')" rules="required">
                <div ref="recaptchaContainer" />
                <input id="recaptcha" v-model="recaptchaValue" name="recaptcha" type="hidden" />

                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <div
              class="d-flex justify-content-start justify-content-lg-between flex-column flex-lg-row align-items-left align-items-lg-center mb-1"
            >
              <!-- checkbox -->
              <b-form-group class="mb-0">
                <b-form-checkbox id="remember-me" v-model="rememberMe" name="checkbox-1">
                  {{ t('Remember Me') }}
                </b-form-checkbox>
              </b-form-group>

              <div
                class="font-small-3 mt-1 mt-lg-0 cursor-pointer"
                @click="routingToForgotPassword"
              >
                {{ t('Forgot Password') }}
              </div>
            </div>

            <!-- submit button -->
            <b-button variant="primary" type="submit" block :disabled="submitting">
              {{ submitting ? t('Loading') : t('Sign In') }}
            </b-button>
            <div class="w-100 d-flex justify-content-center align-content-center">
              <b-badge pill variant="dark mt-1 cursor-pointer" @click="routingToRegister">
                {{ t('Sign Up') }}
              </b-badge>
            </div>
          </b-form>
        </validation-observer>
      </b-card>
    </div>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import {
  BButton,
  BForm,
  BFormInput,
  BFormGroup,
  BCard,
  BLink,
  BCardTitle,
  BCardText,
  BInputGroup,
  BInputGroupAppend,
  BFormCheckbox,
  BBadge
} from 'bootstrap-vue';
import Logo from '@core/layouts/components/Logo.vue';
import { required, email } from '@validations';
import { togglePasswordVisibility } from '@core/mixins/ui/forms';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper';
import useService from '@/auth/service/useService';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'cleave.js/dist/addons/cleave-phone.us';

export default {
  components: {
    BBadge,
    // BSV
    BButton,
    BForm,
    BFormInput,
    BFormGroup,
    BCard,
    BCardTitle,
    BLink,
    Logo,
    BCardText,
    BInputGroup,
    BInputGroupAppend,
    BFormCheckbox,
    ValidationProvider,
    ValidationObserver
  },

  mixins: [togglePasswordVisibility, helper],

  data() {
    return {
      emailValue: '',
      password: '',
      rememberMe: false,
      // validation rules
      required,
      email,
      submitting: false,
      recaptchaWidgetId: null,
      recaptchaValue: ''
    };
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon';
    }
  },

  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  },
  mounted() {
    this.recaptchaWidgetId = this.$refs.recaptchaContainer;
    this.initRecaptcha();
  },

  methods: {
    initRecaptcha() {
      if (typeof grecaptcha === 'object') {
        // eslint-disable-next-line no-undef
        grecaptcha.ready(() => {
          // eslint-disable-next-line no-undef
          grecaptcha.render(this.recaptchaWidgetId, {
            sitekey: process.env.VUE_APP_RECAPTCHA,
            callback: this.recaptchaCallback,
            'expired-callback': this.recaptchaExpiredCallback
          });
        });
      }
    },
    recaptchaCallback(token) {
      this.recaptchaValue = token;
    },
    recaptchaExpiredCallback() {
      this.recaptchaValue = '';
    },

    login() {
      this.$refs.loginForm.validate().then(success => {
        if (success) {
          this.submitting = true;

          useService
            .login({
              email: this.emailValue,
              password: this.password,
              remember_me: this.rememberMe,
              recaptcha: this.recaptchaValue
            })
            .then(async response => {
              useService.setToken(response.data.token);
              useService.setRefreshToken(response.data.refreshToken);

              try {
                const meResponse = await this.$store.dispatch('auth/me');

                if (meResponse.data.customer.status === 0) {
                  this.$router.replace('/pages/profile').then(() => {
                    this.showInfoMessage(this.$t('Customer Activation Document Warning Message'));
                  });
                } else {
                  this.$router.replace('/').then(() => {
                    this.showSuccessMessage(
                      `${meResponse.data.message || this.$t('Login Success Message')}`,
                      `${this.$t('Welcome')} ${
                        meResponse.data.firstName || meResponse.data.username
                      }!`
                    );
                  });
                }
              } finally {
                this.submitting = false;
              }
            })
            .catch(messages => {
              const validMsgs = Array.isArray(messages) ? messages.filter(m => m && m.message) : [];

              if (validMsgs.length) {
                validMsgs.forEach(m => this.showErrorMessage(m.message));
              } else {
                this.showErrorMessage(this.$t('Default Error Message'));
              }

              this.recaptchaReset();
            })
            .finally(() => {
              this.submitting = false;
            });
        }
      });
    },
    routingToRegister() {
      this.$router.push('/register');
    },
    routingToForgotPassword() {
      this.$router.push('/forgot-password');
    },
    recaptchaReset() {
      if (typeof grecaptcha === 'object') {
        // eslint-disable-next-line no-undef
        grecaptcha.reset(this.recaptchaWidgetId);
        this.recaptchaValue = '';
      }
    }
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>
